import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatRequestError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import UpgradeModal from "@/components/UpgradeModal";
import { toast } from "sonner";
import { Play, FloppyDisk, Plus, Minus, Sparkle, X, CheckCircle } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

const KIND_LABEL = {
  exact: "final4b.newplan.match_exact",
  multiple: "final4b.newplan.match_multiple",
  partial: "final4b.newplan.match_partial",
};
const KIND_COLOR = {
  exact: "bg-emerald-100 text-emerald-800 border-emerald-300",
  multiple: "bg-blue-100 text-blue-800 border-blue-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
};

const LOADING_GROUPS = [
  ["container", "Konteynerler"],
  ["trailer", "Tırlar"],
  ["truck", "Kamyonlar"],
];

export default function NewPlan({ mode = "create" }) {
  const { t } = useI18n();
  const { id } = useParams();
  const isEdit = mode === "edit" && !!id;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [pallets, setPallets] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState([]);
  const [selectedLoadingKey, setSelectedLoadingKey] = useState("");
  const [loadingMode, setLoadingMode] = useState("direct");
  const [palletizedPalletTypeId, setPalletizedPalletTypeId] = useState("");
  const [allowPalletStacking, setAllowPalletStacking] = useState(false);
  const [heightOverride, setHeightOverride] = useState("");
  const [form, setForm] = useState({
    shipment_name: "", country: "United Kingdom",
    pallet_type_id: "", delivery_address: "",
    consignee: "", incoterms: "FOB",
    date: new Date().toISOString().slice(0, 10),
    note: "", min_support: 1.0,
  });
  const [qty, setQty] = useState({});
  const [rejectedRecipes, setRejectedRecipes] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [learningSuggestions, setLearningSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [editPlanNo, setEditPlanNo] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeUsage, setUpgradeUsage] = useState(null);
  const [editLock, setEditLock] = useState({ locked: false, owned_by_me: false });
  const editLockTokenRef = useRef(null);
  const editReadOnly = isEdit && editLock.locked && !editLock.owned_by_me;
  const nav = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatRequestError(e, "Ürünler alınamadı")));

    api.get("/product-categories").then((r) => {
      setCategories(r.data);
      if (!isEdit && r.data.length > 0) setSelectedCategory((prev) => prev || r.data[0].id);
    }).catch((e) => toast.error(formatRequestError(e, "Ürün kategorileri alınamadı")));

    api.get("/pallet-types").then((r) => {
      setPallets(r.data);
      if (r.data[0]) {
        setPalletizedPalletTypeId((prev) => prev || r.data[0].id);
      }
      if (!isEdit && r.data[0]) {
        setSelectedLoadingKey(`pallet:${r.data[0].id}`);
        setForm((f) => ({ ...f, pallet_type_id: r.data[0].id }));
      }
    }).catch((e) => toast.error(formatRequestError(e, "Palet tipleri alınamadı")));

    api.get("/loading-types").then((r) => {
      setLoadingTypes([...(r.data?.standard || []), ...(r.data?.custom || [])]);
    }).catch((e) => {
      setLoadingTypes([]);
      toast.error(formatRequestError(e, "Yükleme tipleri alınamadı"));
    });
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api.get(`/plans/${id}`);
        const p = r.data;
        setEditPlanNo(p.plan_no);
        const savedKey = String(p.pallet_type_id || "");
        setSelectedLoadingKey(savedKey.startsWith("loading:") ? savedKey : (savedKey ? `pallet:${savedKey}` : ""));
        setLoadingMode(p.loading_mode || "direct");
        setPalletizedPalletTypeId(p.transport_pallet_type_id || "");
        setAllowPalletStacking(Boolean(p.allow_pallet_stacking));
        setAllowPalletStacking(Boolean(p.allow_pallet_stacking));
        setAllowPalletStacking(Boolean(p.allow_pallet_stacking));
        setAllowPalletStacking(Boolean(p.allow_pallet_stacking));
        setAllowPalletStacking(Boolean(p.allow_pallet_stacking));
        setForm({
          shipment_name: p.shipment_name || "",
          country: p.country || "",
          pallet_type_id: savedKey.startsWith("loading:") ? "" : savedKey,
          delivery_address: p.delivery_address || "",
          consignee: p.consignee || "",
          incoterms: p.incoterms || "FOB",
          date: p.date || new Date().toISOString().slice(0, 10),
          note: p.note || "",
          min_support: p.min_support || 1.0,
        });
        const q = {};
        (p.items || []).forEach((it) => { q[it.sku] = it.quantity; });
        setQty(q);
      } catch (e) { toast.error(formatRequestError(e, t("newplan.plan_load_error"))); }
    })();
  }, [isEdit, id, t]);

  useEffect(() => {
    if (!isEdit) return undefined;
    let alive = true;
    let heartbeat = null;
    const acquire = async () => {
      try {
        const r = await api.post(`/plans/${id}/lock/acquire`);
        if (!alive) return;
        editLockTokenRef.current = r.data.token || null;
        setEditLock(r.data);
        if (r.data.token) {
          heartbeat = window.setInterval(async () => {
            try {
              const hb = await api.post(`/plans/${id}/lock/heartbeat`, { token: editLockTokenRef.current });
              if (alive) setEditLock((prev) => ({ ...prev, expires_at: hb.data.expires_at }));
            } catch (e) {
              if (!alive) return;
              editLockTokenRef.current = null;
              setEditLock(e.response?.data?.detail?.lock || { locked: true, owned_by_me: false });
              toast.error("Plan düzenleme kilidi kaybedildi. Bu ekran salt okunur.");
            }
          }, 30000);
        }
      } catch (e) {
        if (!alive) return;
        setEditLock(e.response?.data?.detail?.lock || { locked: true, owned_by_me: false, user_name: "Başka bir kullanıcı" });
      }
    };
    acquire();
    return () => {
      alive = false;
      if (heartbeat) window.clearInterval(heartbeat);
      const token = editLockTokenRef.current;
      editLockTokenRef.current = null;
      if (token) api.post(`/plans/${id}/lock/release`, { token }).catch(() => {});
    };
  }, [isEdit, id]);

  const items = useMemo(() =>
    Object.entries(qty)
      .filter(([, v]) => v > 0)
      .map(([sku, quantity]) => ({ sku, quantity: Number(quantity) })),
    [qty]);

  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory === "uncategorized") return products.filter((p) => !p.category_id);
    return products.filter((p) => p.category_id === selectedCategory);
  }, [products, selectedCategory]);

  const selectedExternalLoadingType = useMemo(() => {
    if (!selectedLoadingKey.startsWith("loading:")) return null;
    const loadingId = selectedLoadingKey.split(":").slice(2).join(":");
    return loadingTypes.find((row) => row.id === loadingId) || null;
  }, [selectedLoadingKey, loadingTypes]);

  const totalBoxes = items.reduce((s, it) => s + it.quantity, 0);

  const changeLoadingType = (value) => {
    setSelectedLoadingKey(value);
    setResult(null);
    setHeightOverride("");
    if (value.startsWith("pallet:")) {
      setLoadingMode("direct");
      setForm((f) => ({ ...f, pallet_type_id: value.slice("pallet:".length) }));
    } else {
      setForm((f) => ({ ...f, pallet_type_id: "" }));
      setSuggestions([]);
      setLearningSuggestions([]);
      setRejectedRecipes([]);
    }
  };

  const fetchSuggestions = useCallback(async () => {
    if (!form.pallet_type_id || items.length === 0) { setSuggestions([]); setLearningSuggestions([]); setRejectedRecipes([]); return; }
    try {
      const [exact, learned] = await Promise.all([
        api.post("/recipes/suggest", { ...form, items }),
        api.post("/recipes/learning-suggest", { ...form, items }),
      ]);
      setSuggestions(exact.data.suggestions || []);
      setRejectedRecipes(exact.data.rejected_recipes || []);
      setLearningSuggestions(learned.data.recommendations || []);
    } catch (err) { console.warn("recipe suggest failed:", err); }
  }, [form, items]);

  useEffect(() => {
    const timer = setTimeout(fetchSuggestions, 400);
    return () => clearTimeout(timer);
  }, [fetchSuggestions]);

  const applySuggestion = (sug) => {
    // A partial recipe describes covered boxes, not a replacement order.
    setResult(null);
    setSuggestionsOpen(false);
    toast.success(`Sipariş adetleri korundu. Optimize Et ile uygun reçeteler ve kalan ürünler birlikte hesaplanacak.`);
  };

  const optimize = async () => {
    if (items.length === 0) return toast.error(t("newplan.no_product"));
    setLoading(true); setResult(null);
    try {
      let r;
      if (selectedExternalLoadingType) {
        if (!selectedExternalLoadingType.height && !(Number(heightOverride) > 0)) {
          toast.error("Bu araç tipinin yüksekliği değişkendir. İç yükseklik değerini girin.");
          return;
        }
        if (loadingMode === "palletized" && !palletizedPalletTypeId) {
          toast.error("Paletli yükleme için palet tipi seçin.");
          return;
        }
        const loadingPayload = {
          loading_type_id: selectedExternalLoadingType.id,
          category: selectedExternalLoadingType.category,
          height_override: selectedExternalLoadingType.height ? null : Number(heightOverride),
          min_support: form.min_support,
          items,
          loading_mode: loadingMode,
        };
        if (loadingMode === "palletized") {
          loadingPayload.pallet_type_id = palletizedPalletTypeId;
          loadingPayload.allow_pallet_stacking = allowPalletStacking;
        }
        r = await api.post(loadingMode === "palletized" ? "/loading/optimize-palletized" : "/loading/optimize", loadingPayload);
      } else {
        if (!form.pallet_type_id) return toast.error("Geçerli bir yükleme tipi seçin.");
        r = await api.post("/plans/optimize", { ...form, items });
      }
      setResult(r.data);
      const applied = r.data.applied_recipes || [];
      if (selectedExternalLoadingType) {
        if (loadingMode === "palletized") {
          toast.success(`${r.data.transport_pallet_count || 0} palet → ${r.data.pallets.length} ${String(r.data.unit_label || "yükleme birimi").toLocaleLowerCase("tr-TR")} · H3POE-PALLETIZED tamamlandı`);
        } else {
          toast.success(`${r.data.unit_label || "Yükleme alanı"}: ${r.data.pallets.length} birim · H3POE-LARGE tamamlandı`);
        }
      } else if (applied.length) {
        toast.success(`${applied.length} ${t("final4b.newplan.recipes_applied")} + ${r.data.pallets.length} ${t("newplan.pallet_count")}`);
      } else {
        toast.success(`${t("final4b.newplan.optimization_done")}: ${r.data.pallets.length} ${t("newplan.pallet_count")}`);
      }
    } catch (e) {
      toast.error(formatRequestError(e));
    } finally { setLoading(false); }
  };

  const savePlan = async () => {
    if (!result) return;
    if ((result.pallets || []).some((p) => p.overweight)) return toast.error("Ağırlık kapasitesini aşan yükleme birimi kaydedilemez.");
    const pallet = pallets.find((p) => p.id === form.pallet_type_id);
    const loadingSpace = result.loading_type || selectedExternalLoadingType;
    if (!pallet && !loadingSpace) return toast.error("Plan kaydı için geçerli bir yükleme tipi seçin.");
    try {
      const body = {
        ...form,
        pallet_type_id: loadingSpace ? selectedLoadingKey : pallet.id,
        loading_mode: loadingSpace ? loadingMode : "direct",
        transport_pallet_type_id: loadingSpace && loadingMode === "palletized" ? palletizedPalletTypeId : null,
        allow_pallet_stacking: loadingSpace && loadingMode === "palletized" ? allowPalletStacking : false,
        stage1_pallets: loadingSpace && loadingMode === "palletized" ? (result.stage1_pallets || []) : [],
        items,
        pallet_L: loadingSpace ? Number(loadingSpace.length) : pallet.length,
        pallet_W: loadingSpace ? Number(loadingSpace.width) : pallet.width,
        pallet_H: loadingSpace ? Number(loadingSpace.height) : pallet.usable_height,
        pallets: result.pallets,
        unplaced: result.unplaced,
        fill_ratio: result.fill_ratio,
      };
      const r = await api.post("/plans", body);
      toast.success(t("newplan.plan_saved"));
      nav(`/plans/${r.data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        const d = e.response.data?.detail || {};
        setUpgradeUsage({ limit: d.limit, used: d.used, subscription: d.subscription });
        setUpgradeOpen(true);
      } else {
        toast.error(formatRequestError(e));
      }
    }
  };

  const reoptimize = async () => {
    if (editReadOnly) return toast.error(`Plan ${editLock.user_name || "başka bir kullanıcı"} tarafından düzenleniyor.`);
    if (selectedLoadingKey.startsWith("loading:")) return toast.error("Büyük yükleme planlarının revize optimizasyonu bir sonraki adımda etkinleştirilecek.");
    if (items.length === 0) return toast.error(t("newplan.no_product"));
    if (!window.confirm(`${editPlanNo}: ${t("final4b.newplan.reopt_confirm")}`)) return;
    setLoading(true);
    try {
      const r = await api.put(
        `/plans/${id}/reoptimize`,
        { items, min_support: form.min_support },
        { headers: { "X-Plan-Lock-Token": editLockTokenRef.current || "" } },
      );
      toast.success(`${t("final4b.newplan.plan_updated")}: ${r.data.pallets.length} ${t("newplan.pallet_count")}`);
      nav(`/plans/${id}`);
    } catch (e) {
      toast.error(formatRequestError(e));
    } finally { setLoading(false); }
  };

  const resultUnitLabel = selectedExternalLoadingType ? (result?.unit_label || "Yükleme Birimi") : "Palet";

  return (
    <div>
      <PageHeader
        title={isEdit ? `${t("newplan.edit_title")} · ${editPlanNo}` : t("newplan.create_title")}
        subtitle={isEdit ? t("newplan.edit_subtitle") : t("newplan.create_subtitle")} />
      {isEdit && editLock.locked && (
        <div className={`mx-4 sm:mx-8 mt-4 border px-4 py-3 text-sm font-semibold ${editReadOnly ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
          {editReadOnly ? `${editLock.user_name || "Başka bir kullanıcı"} bu planı düzenliyor. Revize ekranı salt okunur.` : "Düzenleme kilidi sende · Revize güvenli."}
        </div>
      )}
      <div className={`p-4 sm:p-8 grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 ${editReadOnly ? "pointer-events-none opacity-70" : ""}`}>
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 p-5 space-y-3">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("newplan.shipment_info")}</h3>
            <Field label={t("newplan.shipment_name")}>
              <input data-testid="input-shipment" className="input" value={form.shipment_name}
                onChange={(e) => setForm({ ...form, shipment_name: e.target.value })} disabled={isEdit} />
            </Field>
            <Field label={t("newplan.consignee")}>
              <input data-testid="input-consignee" className="input"
                placeholder={t("final4b.newplan.consignee_example")}
                value={form.consignee}
                onChange={(e) => setForm({ ...form, consignee: e.target.value })} disabled={isEdit} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("newplan.country")}>
                <input data-testid="input-country" className="input" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} disabled={isEdit} />
              </Field>
              <Field label="INCOTERMS">
                <select data-testid="input-incoterms" className="input" value={form.incoterms}
                  onChange={(e) => setForm({ ...form, incoterms: e.target.value })} disabled={isEdit}>
                  {["EXW", "FCA", "FOB", "CIF", "CIP", "CFR", "DAP", "DDP", "DPU"].map((term) => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Yükleme Tipi">
              <select data-testid="input-loading-type" className="input" value={selectedLoadingKey}
                onChange={(e) => changeLoadingType(e.target.value)} disabled={isEdit}>
                <optgroup label="Paletler">
                  {pallets.map((p) => (
                    <option key={`pallet:${p.id}`} value={`pallet:${p.id}`}>{p.name} ({p.length}×{p.width}×{p.total_height} cm)</option>
                  ))}
                </optgroup>
                {LOADING_GROUPS.map(([category, label]) => {
                  const rows = loadingTypes.filter((row) => row.category === category);
                  if (!rows.length) return null;
                  return (
                    <optgroup key={category} label={label}>
                      {rows.map((row) => (
                        <option key={`loading:${category}:${row.id}`} value={`loading:${category}:${row.id}`}>
                          {row.name} ({row.length}×{row.width}×{row.height || "değişken"} cm){row.system ? "" : " · Özel"}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              {selectedExternalLoadingType && (
                <div className="mt-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5">
                  <b>{selectedExternalLoadingType.name}</b> · {loadingMode === "palletized" ? "H3POE-PALLETIZED" : "H3POE-LARGE"} aktif · Maks. yük: <b>{Number(selectedExternalLoadingType.max_weight_kg || 0).toLocaleString("tr-TR")} kg</b>
                </div>
              )}
            </Field>
            {selectedExternalLoadingType && (
              <>
                <Field label="Yükleme Şekli">
                  <select data-testid="input-loading-mode" className="input" value={loadingMode}
                    onChange={(e) => { setLoadingMode(e.target.value); setResult(null); }} disabled={isEdit}>
                    <option value="direct">Direkt / Paletsiz Yükleme</option>
                    <option value="palletized">Paletli Yükleme</option>
                  </select>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {loadingMode === "palletized" ? "İki aşama: Ürün/Koli → Palet → Araç" : "Ürün/Koli doğrudan seçilen araca yerleştirilir."}
                  </div>
                </Field>
                {loadingMode === "palletized" && (
                  <Field label="Palet Tipi">
                    <select data-testid="input-palletized-pallet-type" className="input" value={palletizedPalletTypeId}
                      onChange={(e) => { setPalletizedPalletTypeId(e.target.value); setResult(null); }} disabled={isEdit}>
                      <option value="">Palet tipi seçin</option>
                      {pallets.map((p) => (
                        <option key={`transport-pallet:${p.id}`} value={p.id}>
                          {p.name} ({p.length}×{p.width}×{p.total_height} cm)
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                {loadingMode === "palletized" && (
                  <label className="flex items-start gap-2 border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="input-pallet-stacking-wrap">
                    <input type="checkbox" data-testid="input-pallet-stacking" checked={allowPalletStacking}
                      onChange={(e) => { setAllowPalletStacking(e.target.checked); setResult(null); }} disabled={isEdit} />
                    <span><b>Paletleri üst üste yüklemeye izin ver</b><br/><span className="text-slate-500">Açık olduğunda araç içinde en fazla 2 palet üst üste yerleşebilir. Kapalı olduğunda tüm paletler tabanda kalır.</span></span>
                  </label>
                )}
              </>
            )}
            {selectedExternalLoadingType && !selectedExternalLoadingType.height && (
              <Field label="İç Yükseklik (cm)">
                <input data-testid="input-loading-height" type="number" min="1" step="1" className="input"
                  placeholder="Örn. 260" value={heightOverride}
                  onChange={(e) => setHeightOverride(e.target.value)} disabled={isEdit} />
              </Field>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("newplan.date")}>
                <input data-testid="input-date" type="date" className="input" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={isEdit} />
              </Field>
              <Field label="Destek %">
                <select data-testid="input-support" className="input" value={form.min_support}
                  onChange={(e) => setForm({ ...form, min_support: Number(e.target.value) })}>
                  <option value={1.0}>100%</option><option value={0.9}>90%</option><option value={0.8}>80%</option><option value={0.7}>70%</option>
                </select>
                <p className="mt-1 text-xs text-slate-500">Ürün kartında daha yüksek minimum destek varsa o sınır uygulanır.</p>
              </Field>
            </div>
            {!isEdit && (
              <>
                <Field label={t("newplan.delivery_address")}>
                  <textarea data-testid="input-address" className="input" rows={2} value={form.delivery_address}
                    onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} />
                </Field>
                <Field label={t("newplan.note")}>
                  <textarea data-testid="input-note" className="input" rows={2} value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </Field>
              </>
            )}
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("newplan.product_qty")}</h3>
              <div className="text-xs text-slate-500 font-mono">
                {t("newplan.total")}: <b className="text-slate-900">{totalBoxes}</b>
              </div>
            </div>

            <div className="mb-3">
              <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block mb-1">
                {t("newplan.product_category")}
              </label>
              <select data-testid="plan-category-filter" className="input" value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="">{t("newplan.select_category")}</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="uncategorized">Kategorisiz</option>
              </select>
            </div>

            <div className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
              {filteredProducts.map((p) => (
                <div key={p.id} className="flex items-center gap-2" data-testid={`qty-row-${p.sku}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{p.name || p.sku}</div>
                    <div className="text-[10px] font-mono text-slate-500">{p.sku}{(p.attributes?.inch || p.inch) ? ` · ${p.attributes?.inch || p.inch}"` : ""}</div>
                  </div>
                  <div className="flex items-center border border-slate-300 shrink-0">
                    <button onClick={() => setQty({ ...qty, [p.sku]: Math.max(0, (qty[p.sku] || 0) - 1) })}
                      className="w-7 h-8 hover:bg-slate-100"><Minus size={12} /></button>
                    <input data-testid={`qty-${p.sku}`} type="text" inputMode="numeric" pattern="[0-9]*"
                      className="w-14 sm:w-16 text-center h-8 font-mono text-sm border-l border-r border-slate-300 outline-none focus:bg-blue-50"
                      value={qty[p.sku] ?? 0}
                      onFocus={(e) => { if (Number(qty[p.sku] || 0) === 0) e.currentTarget.select(); }}
                      onChange={(e) => { const raw = e.target.value.replace(/\D/g, ""); setQty({ ...qty, [p.sku]: raw === "" ? "" : Number(raw) }); }}
                      onBlur={() => { if (qty[p.sku] === "") setQty({ ...qty, [p.sku]: 0 }); }} />
                    <button onClick={() => setQty({ ...qty, [p.sku]: (qty[p.sku] || 0) + 1 })}
                      className="w-7 h-8 hover:bg-slate-100"><Plus size={12} /></button>
                  </div>
                </div>
              ))}
              {selectedCategory && filteredProducts.length === 0 && (
                <div className="text-center text-xs text-slate-400 py-8">{t("newplan.no_category_products")}</div>
              )}
              {!selectedCategory && (
                <div className="text-center text-xs text-slate-400 py-8">{t("newplan.choose_category")}</div>
              )}
            </div>
          </div>
          <button data-testid={isEdit ? "reoptimize-btn" : "optimize-btn"}
            onClick={isEdit ? reoptimize : optimize} disabled={loading || items.length === 0 || editReadOnly}
            className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white py-3 font-semibold rounded-sm inline-flex items-center justify-center gap-2">
            <Play size={18} weight="fill" /> {loading ? t("newplan.calculating") : (isEdit ? t("newplan.reoptimize") : t("newplan.optimize"))}
          </button>
        </div>

        <div className="xl:col-span-2 space-y-4 min-w-0">
          {suggestions.length > 0 && (
            <div className="border border-emerald-300 bg-gradient-to-r from-emerald-50 to-white p-4" data-testid="recipe-suggest-banner">
              <div className="flex items-start gap-3">
                <div className="bg-emerald-600 text-white p-2 rounded-full flex-shrink-0"><Sparkle size={18} weight="fill" /></div>
                <div className="flex-1">
                  <div className="font-display font-bold text-emerald-900">
                    {t("final4b.newplan.suitable_count")}: {suggestions.length}
                  </div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    {t("final4b.newplan.best_match")}: <b>{t(KIND_LABEL[suggestions[0].kind])}</b>
                    {suggestions[0].kind === "multiple" && ` (${suggestions[0].times}×)`}
                    · {suggestions[0].pallet_count} reçete paleti
                    {suggestions[0].kind === "partial" && " · kalan kutular ayrıca hesaplanır"}
                    {suggestions[0].equivalent_dimensions_used && " · Aynı ölçülü kutu eşleşmesi"}
                    {suggestions[0].inventory_adjusted && " · Eski adet kaydı yerine yerleşimdeki gerçek kutular kullanılıyor"}
                  </div>
                </div>
                <button data-testid="suggest-open" onClick={() => setSuggestionsOpen(true)}
                  className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2">
                  {t("final4b.newplan.inspect")}
                </button>
              </div>
            </div>
          )}

          {learningSuggestions.length > 0 && (
            <div className="border border-violet-300 bg-violet-50 p-4" data-testid="learning-suggest-banner">
              <div className="flex items-start gap-3">
                <div className="bg-violet-600 text-white p-2 rounded-full"><Sparkle size={18} weight="fill" /></div>
                <div className="flex-1">
                  <div className="font-display font-bold text-violet-900">Learning Engine önerisi</div>
                  <div className="text-xs text-violet-800 mt-1">
                    Geçmiş onaylı reçete: <b>{learningSuggestions[0].shipment_name || learningSuggestions[0].recipe_id}</b> · 
                    güven %{Math.round((learningSuggestions[0].confidence || 0) * 100)} · 
                    kutu/SKU örtüşmesi %{Math.round((learningSuggestions[0].metrics?.sku_overlap || 0) * 100)} · 
                    miktar örtüşmesi %{Math.round((learningSuggestions[0].metrics?.quantity_overlap || 0) * 100)}
                  </div>
                  <div className="text-[11px] text-violet-700 mt-1">Aynı fiziksel ölçüdeki ürünler öğrenme eşleşmesine dahil edilir. Yaklaşık eşleşme yine otomatik uygulanmaz.</div>
                </div>
              </div>
            </div>
          )}

          {rejectedRecipes.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 p-3 text-sm text-amber-950" data-testid="recipe-rejections">
              <b>Eşleşen reçete neden uygulanmadı?</b>
              {rejectedRecipes.map((r) => (
                <div key={r.recipe_id} className="mt-2">
                  <b>{r.shipment_name || "Reçete"}</b>: {r.message}
                  {r.pallet_no != null && ` · Palet ${r.pallet_no}`}
                  {r.box_no != null && ` · Kutu ${r.box_no}`}
                  {r.sku && ` · ${r.sku}`}
                  {r.actual_support_pct != null && ` · Gerçek destek %${r.actual_support_pct}, gereken %${r.required_support_pct} (plan %${r.plan_support_pct}, ürün %${r.product_support_pct})`}
                  <details className="mt-1 text-xs"><summary>Kontrol değerleri</summary><pre className="whitespace-pre-wrap">{JSON.stringify(r, null, 2)}</pre></details>
                </div>
              ))}
            </div>
          )}

          {!result && (
            <div className="bg-white border border-slate-200 p-8 sm:p-12 text-center text-slate-400">
              <div className="font-display text-lg font-bold text-slate-500">{t("newplan.result_waiting")}</div>
              <div className="text-sm mt-1">{t("final4b.newplan.enter_qty")} <b>{isEdit ? t("final4b.newplan.reoptimize") : t("final4b.newplan.optimize")}</b> {t("final4b.newplan.press_button")}</div>
            </div>
          )}
          {result && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric label={selectedExternalLoadingType ? `${resultUnitLabel} Sayısı` : t("newplan.pallet_count")} value={result.pallets.length} accent="#2563EB" />
                <Metric label="Doluluk" value={`${((result.fill_ratio || 0) * 100).toFixed(1)}%`} accent="#16A34A" />
                <Metric label={t("newplan.unplaced")} value={result.unplaced.length} accent={result.unplaced.length ? "#DC2626" : "#64748B"} />
                <Metric label="Strateji" value={result.engine || result.strategy || "—"} accent="#F59E0B" mono />
              </div>
              {selectedExternalLoadingType && loadingMode === "palletized" && result.stage1_pallets && (
                <div className="bg-violet-50 border border-violet-200 p-3 text-sm text-violet-900" data-testid="palletized-result-banner">
                  <b>Paletli yükleme tamamlandı.</b> Aşama 1: <b>{result.transport_pallet_count || result.stage1_pallets.length} palet</b> hazırlandı · Aşama 2: <b>{result.pallets.length} {String(resultUnitLabel).toLocaleLowerCase("tr-TR")}</b> içine yerleştirildi.
                </div>
              )}
              {selectedExternalLoadingType && result.capacity_enforced && (
                <div className="bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900" data-testid="capacity-enforced-banner">
                  <b>Ağırlık kapasitesi aktif.</b> Her {resultUnitLabel.toLowerCase()} en fazla <b>{Number(result.weight_capacity_kg || 0).toLocaleString("tr-TR")} kg</b> olacak şekilde ayrıldı. {loadingMode === "palletized" ? "Paletler araç tabanına üst üste bindirilmeden yerleştirilir." : "Hacim veya ağırlık limiti dolduğunda H3POE-LARGE otomatik yeni yükleme birimi açar."}
                </div>
              )}
              {result.applied_recipes && result.applied_recipes.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm p-3 rounded-sm" data-testid="applied-recipes-badge">
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                    {result.applied_recipes.length} {t("final4b.newplan.recipes_applied")}
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs">
                    {result.applied_recipes.map((ar, i) => (
                      <div key={i} className="font-mono">
                        <span className={`inline-block px-1.5 py-0.5 border rounded-sm ${KIND_COLOR[ar.kind]}`}>{KIND_LABEL[ar.kind]}</span>
                        {" "}× {ar.times} → {ar.pallet_count} palet
                        {ar.equivalent_dimensions_used && " · aynı ölçülü SKU remap"}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="data-table w-full min-w-[720px]">
                  <thead>
                    <tr><th>{selectedExternalLoadingType ? "Yükleme Birimi" : t("newplan.pallet_type")}</th><th className="text-right">{selectedExternalLoadingType && loadingMode === "palletized" ? "Palet" : t("newplan.box")}</th><th className="text-right">{t("newplan.weight")}</th>
                        {selectedExternalLoadingType && <th className="text-right">Kg Kapasitesi</th>}
                        <th className="text-right">{t("newplan.height")}</th><th className="text-right">{t("newplan.fill")}</th><th className="text-right">{t("newplan.empty_volume")}</th></tr>
                  </thead>
                  <tbody>
                    {result.pallets.map((p) => (
                      <tr key={p.pallet_no}>
                        <td className="font-mono text-xs font-bold">{selectedExternalLoadingType ? `${resultUnitLabel} ${p.pallet_no}` : `P${p.pallet_no}`}
                          {p.from_recipe && <span className="ml-1 text-[9px] px-1 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-sm">{t("final4b.newplan.recipe_badge")}</span>}
                        </td>
                        <td className="text-right data-cell">{p.box_count}</td>
                        <td className="text-right data-cell">{p.total_weight} kg</td>
                        {selectedExternalLoadingType && <td className="text-right data-cell"><CapacityBadge unit={p} /></td>}
                        <td className="text-right data-cell">{p.top_height} cm</td>
                        <td className="text-right data-cell">{(p.fill_ratio * 100).toFixed(1)}%</td>
                        <td className="text-right data-cell text-slate-500">{p.empty_volume.toFixed(0)} cm³</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!isEdit && (
                <button data-testid="save-plan-btn" onClick={savePlan}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white px-5 py-2.5 text-sm font-semibold rounded-sm">
                  <FloppyDisk size={16} weight="bold" /> {t("final4b.newplan.save_open")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {suggestionsOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={() => setSuggestionsOpen(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[75vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">{t("newplan.suitable_recipes")}</h3>
              <button onClick={() => setSuggestionsOpen(false)} data-testid="suggest-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {suggestions.map((s) => (
                <div key={s.recipe_id} className="border border-slate-200 p-3 flex items-start gap-3 hover:border-emerald-400" data-testid={`suggest-item-${s.recipe_id}`}>
                  <div className={`text-[10px] uppercase font-bold px-2 py-1 border rounded-sm ${KIND_COLOR[s.kind]}`}>
                    {t(KIND_LABEL[s.kind])}{s.kind === "multiple" && ` ×${s.times}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900">{s.shipment_name || t("final4b.newplan.recipe")}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">
                      {s.items.map((it) => `${it.sku}×${it.quantity}`).join(" · ")}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {t("final4b.newplan.consumed")}: <b>{Object.entries(s.consumed).map(([k, v]) => `${k}×${v}`).join(", ")}</b>
                      {" · "}{s.pallet_count} reçete paleti
                      {s.kind === "partial" && ` · ${totalBoxes - Object.values(s.consumed).reduce((sum, quantity) => sum + quantity, 0)} kutu ayrıca optimize edilecek`}
                      {s.equivalent_dimensions_used && " · aynı fiziksel ölçü"}
                      {s.inventory_adjusted && " · Gerçek yerleşim adetleri esas alındı; siparişin tüm adetleri korunur"}
                    </div>
                  </div>
                  <button data-testid={`suggest-apply-${s.recipe_id}`} onClick={() => applySuggestion(s)}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 font-semibold whitespace-nowrap">
                    Uygula
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`.input { width:100%; border:1px solid #cbd5e1; padding:0.45rem 0.65rem; border-radius:0.125rem; font-size:0.85rem; outline:none; }
              .input:focus { border-color:#2563EB; box-shadow:0 0 0 3px rgba(37,99,235,0.15); }
              .input:disabled { background:#F8FAFC; color:#64748B; cursor:not-allowed; }`}</style>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} usage={upgradeUsage} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Metric({ label, value, accent, mono }) {
  return (
    <div className="bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-0.5 data-cell ${mono ? "font-mono text-lg" : ""}`} style={{ color: accent }}>{value}</div>
    </div>
  );
}

function CapacityBadge({ unit }) {
  const ratio = Number(unit.weight_utilization || 0);
  const pct = Math.min(100, ratio * 100);
  const status = unit.overweight ? "over" : (unit.capacity_status || "normal");
  const cls = status === "over" || status === "limit" ? "bg-red-100 text-red-800 border-red-300" : status === "critical" ? "bg-orange-100 text-orange-800 border-orange-300" : status === "warning" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const label = status === "over" ? "AŞIRI YÜK" : status === "limit" ? "%100 LİMİT" : status === "critical" ? `Kritik %${pct.toFixed(0)}` : status === "warning" ? `Uyarı %${pct.toFixed(0)}` : `%${pct.toFixed(1)}`;
  return <div className="inline-flex flex-col items-end gap-1" data-testid={`weight-capacity-${unit.pallet_no}`}>
    <span className={`text-[10px] font-bold border px-1.5 py-0.5 ${cls}`}>{label}</span>
    <span className="text-[10px] text-slate-500 whitespace-nowrap">{Number(unit.total_weight || 0).toLocaleString("tr-TR")} / {Number(unit.weight_capacity_kg || 0).toLocaleString("tr-TR")} kg</span>
  </div>;
}


